import {
  Accordion,
  Button,
  Field,
  Spinner,
} from '@noorden-insurtech/alba-design-system';
import { formatPercent } from '@/shared/utils/format-percent.util';
import { ArrowLeft, Calculator, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PremiumCard } from '../../proposals/components/proposal-steps/premium-card.component';
import type { BusinessLocation } from '../../quotes/components/quote-steps/business-location';
import { BusinessLocationCard } from '../../quotes/components/quote-steps/business-location-card.component';
import type { AnalysisQuote } from '../types';

type PaymentInfoEntry = {
  installmentPeriodCount: number;
  installmentType?: string;
  installmentList?: Array<{ installmentAmount: number }>;
};

interface Step4AnalysisCalculationProps {
  quote: AnalysisQuote;
  onBack: () => void;
  onReject: (description: string) => void;
  onSubmit: () => void;
  onExit: () => void;
  isSubmitting?: boolean;
  /**
   * Locais já mapeados como `BusinessLocation`, com coberturas, assistências
   * e respostas do questionário, para exibir o cartão completo dentro do
   * step de cálculo da análise.
   */
  locations: BusinessLocation[];
  /**
   * `productId` do InsureMO usado pelo `BusinessLocationCard` para resolver
   * o nome do pacote de assistências contratado.
   */
  productId?: string;
  /**
   * Lista de opções de parcelamento devolvida pelo InsureMO após o cálculo.
   * Cada entrada vira uma linha (`À vista`, `1+1`, `1+2`, …) na tabela
   * "Opções de Parcelamento".
   */
  paymentInfo?: PaymentInfoEntry[];
  /**
   * Dispara um novo cálculo. Acionado pelo botão Calculator ao lado dos
   * percentuais de agravo/desconto. Quando o usuário preenche um dos campos
   * de flexibilidade, o tipo/percentual é repassado para ser aplicado sobre o
   * prêmio líquido no recálculo.
   */
  onRecalculate?: (discountLoading?: DiscountLoading) => void;
}

export type DiscountLoading = {
  discountLoadingType: 'Desconto' | 'Agravo';
  discountLoadingRate: number;
};

/** Percentual máximo permitido para desconto. Agravo não tem teto. */
const MAX_PERCENT = 100;

/**
 * Converte o texto digitado (aceita vírgula decimal) num percentual positivo.
 * Retorna `undefined` quando vazio ou inválido — sinaliza "campo não preenchido".
 */
function parsePercentInput(input: string): number | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  const value = parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * Higieniza a digitação do percentual: mantém apenas dígitos e um separador
 * decimal e, quando `max` é informado, limita o valor a esse teto. Preserva a
 * digitação parcial (ex.: "10," ou "10.5") quando ainda dentro do limite.
 */
function sanitizePercentInput(raw: string, max?: number): string {
  const cleaned = raw.replace(/[^0-9.,]/g, '');
  if (max === undefined) return cleaned;
  const value = parseFloat(cleaned.replace(',', '.'));
  if (Number.isFinite(value) && value > max) {
    return String(max);
  }
  return cleaned;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function Step4AnalysisCalculation({
  quote,
  onBack,
  onReject,
  onSubmit,
  onExit,
  isSubmitting = false,
  locations,
  productId,
  paymentInfo,
  onRecalculate,
}: Step4AnalysisCalculationProps) {
  const { calculation, brokerName, product, quoteNumber } = quote;

  const [additionalPercent, setAdditionalPercent] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [observations, setObservations] = useState('');

  // Mutuamente exclusivos: ao preencher um campo, o outro é desabilitado para
  // impedir a aplicação simultânea de agravo e desconto.
  const hasDiscount = discountPercent.trim() !== '';
  const hasLoading = additionalPercent.trim() !== '';

  const handleRecalculate = () => {
    if (!onRecalculate) return;

    const discountRate = parsePercentInput(discountPercent);
    if (discountRate !== undefined) {
      onRecalculate({
        discountLoadingType: 'Desconto',
        discountLoadingRate: discountRate,
      });
      return;
    }

    const loadingRate = parsePercentInput(additionalPercent);
    if (loadingRate !== undefined) {
      onRecalculate({
        discountLoadingType: 'Agravo',
        discountLoadingRate: loadingRate,
      });
      return;
    }

    onRecalculate();
  };

  const installmentRows = useMemo(() => {
    if (!paymentInfo?.length) return [];
    const sorted = [...paymentInfo].sort(
      (a, b) => a.installmentPeriodCount - b.installmentPeriodCount
    );
    return sorted.map(pi => {
      const count = pi.installmentPeriodCount;
      const firstAmount = pi.installmentList?.[0]?.installmentAmount ?? 0;
      const totalPremium =
        pi.installmentList?.reduce(
          (sum, inst) => sum + (inst.installmentAmount ?? 0),
          0
        ) ?? 0;
      const hasInterest =
        pi.installmentType !== '-1' && pi.installmentType !== '0';

      return {
        label: count === 1 ? 'À vista' : `1+${count - 1}`,
        interestLabel: hasInterest ? `${pi.installmentType}%` : 'Sem Juros',
        installmentValue: formatCurrency(firstAmount),
        totalValue: formatCurrency(totalPremium),
      };
    });
  }, [paymentInfo]);

  const locationIds = useMemo(() => locations.map(loc => loc.id), [locations]);
  const [openLocations, setOpenLocations] = useState<Set<string>>(new Set());

  const allLocationsOpen =
    openLocations.size === locationIds.length && locationIds.length > 0;

  const toggleAllLocations = () => {
    setOpenLocations(
      allLocationsOpen ? new Set() : new Set(locationIds)
    );
  };

  const toggleLocation = (id: string) => {
    setOpenLocations(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className='relative flex flex-col gap-8 rounded-2xl bg-alba-white-500 p-4 shadow-[0px_4px_24px_0px_rgba(15,22,30,0.08)] sm:p-8'>
      {isSubmitting && (
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-alba-white-500/80 backdrop-blur-sm'>
          <Spinner size='lg' colorScheme='blue' />
          <span className='font-montserrat text-sm font-medium text-alba-black-500'>
            Recalculando cotação...
          </span>
        </div>
      )}
      <div className='flex w-full justify-center'>
        <PremiumCard
          totalValue={formatCurrency(calculation.totalPremium)}
          liquidValue={formatCurrency(calculation.netPremium)}
          interestPercentage='0,00%'
          interestValue=''
          iofValue={formatCurrency(calculation.iof)}
          product={product}
          proposalNumber={quoteNumber}
          status='Em Análise'
          showPisCofins={false}
        />
      </div>

      <div className='flex flex-col gap-4'>
        <span className='font-montserrat text-base font-normal text-alba-black-500'>
          Condições comerciais
        </span>

        <div className='flex flex-col gap-3 lg:flex-row lg:items-end'>
          <div className='grid flex-1 grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-10'>
            <Field.Root>
              <Field.Label>Percentual de Agravo</Field.Label>
              <Field.Input
                type='text'
                inputMode='decimal'
                pattern='[0-9]*[.,]?[0-9]*'
                value={additionalPercent}
                onChange={e =>
                  setAdditionalPercent(sanitizePercentInput(e.target.value))
                }
                placeholder='%'
                disabled={hasDiscount || isSubmitting}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>Percentual de Desconto</Field.Label>
              <Field.Input
                type='text'
                inputMode='decimal'
                pattern='[0-9]*[.,]?[0-9]*'
                value={discountPercent}
                onChange={e =>
                  setDiscountPercent(
                    sanitizePercentInput(e.target.value, MAX_PERCENT)
                  )
                }
                placeholder='%'
                disabled={hasLoading || isSubmitting}
              />
            </Field.Root>
          </div>
          <button
            type='button'
            aria-label='Recalcular'
            onClick={handleRecalculate}
            disabled={isSubmitting || !onRecalculate}
            className='flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-alba-green-500 text-sm font-medium text-alba-white-500 shadow-sm transition-colors hover:bg-alba-green-600 disabled:cursor-not-allowed disabled:opacity-60 lg:w-10'
          >
            <Calculator className='h-5 w-5' strokeWidth={1.6} />
            <span className='lg:hidden'>Recalcular</span>
          </button>
        </div>

        <Accordion.Root collapsible defaultValue={['installments']}>
          <Accordion.Item
            value='installments'
            className='rounded-2xl border border-alba-gray-200 shadow-none'
          >
            <Accordion.ItemTrigger className='flex w-full items-center justify-between px-5 py-4 font-montserrat text-sm font-semibold text-alba-black-500'>
              Opções de Parcelamento
              <Accordion.ItemIndicator>
                <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
              </Accordion.ItemIndicator>
            </Accordion.ItemTrigger>
            <Accordion.ItemContent className='border-t border-alba-gray-200'>
              {installmentRows.length === 0 ? (
                <div className='px-5 py-4 font-montserrat text-sm text-alba-gray-600'>
                  As opções de parcelamento para esta cotação serão exibidas
                  aqui após o cálculo.
                </div>
              ) : (
                <div className='w-full overflow-hidden'>
                  <div className='flex items-center gap-2 rounded-t-xl bg-alba-gray-200 px-3 py-3 sm:rounded-none sm:px-6'>
                    <span className='flex-1 font-montserrat text-[11px] font-bold text-alba-black-500 sm:text-xs'>
                      N de Parcelas
                    </span>
                    <span className='flex-1 text-center font-montserrat text-[11px] font-bold text-alba-black-500 sm:text-xs'>
                      Percentual de Juros
                    </span>
                    <span className='flex-1 text-center font-montserrat text-[11px] font-bold text-alba-black-500 sm:text-xs'>
                      Valor da Parcela
                    </span>
                    <span className='flex-1 text-center font-montserrat text-[11px] font-bold text-alba-black-500 sm:text-right sm:text-xs'>
                      Total
                    </span>
                  </div>

                  {installmentRows.map((row, index) => (
                    <div key={row.label}>
                      {index > 0 && (
                        <div className='h-px w-full bg-alba-gray-200' />
                      )}
                      <div className='flex items-center gap-2 px-3 py-4 sm:px-6'>
                        <span className='flex-1 font-montserrat text-xs text-alba-black-500 sm:text-sm'>
                          {row.label}
                        </span>
                        <span className='flex-1 text-center font-montserrat text-xs text-alba-gray-600 sm:text-sm'>
                          {row.interestLabel}
                        </span>
                        <span className='flex-1 text-center font-montserrat text-xs text-alba-black-500 sm:text-sm'>
                          {row.installmentValue}
                        </span>
                        <span className='flex-1 text-center font-montserrat text-xs text-alba-gray-600 sm:text-right sm:text-sm'>
                          {row.totalValue}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>
      </div>

      <div className='flex flex-col gap-3'>
        <span className='font-montserrat text-base font-normal text-alba-black-500'>
          Observações da Análise
        </span>
        <Field.Root>
          <Field.Textarea
            rows={5}
            value={observations}
            onChange={e => setObservations(e.target.value)}
            placeholder='Digite observações sobre a inspeção...'
          />
        </Field.Root>
      </div>

      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <span className='font-montserrat text-sm text-alba-black-500'>
            Lista de Locais
          </span>
          <button
            type='button'
            onClick={toggleAllLocations}
            disabled={locationIds.length === 0}
            className='flex items-center gap-1 font-montserrat text-sm text-alba-blue-500 transition-colors hover:text-alba-green-500 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {allLocationsOpen ? 'Fechar Todos' : 'Abrir Todos'} (
            {locationIds.length})
            {allLocationsOpen ? (
              <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
            ) : (
              <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
            )}
          </button>
        </div>

        <div className='flex flex-col gap-3'>
          {locations.map((location, index) => (
            <BusinessLocationCard
              key={location.id}
              location={location}
              locationNumber={index + 1}
              isOpen={openLocations.has(location.id)}
              canEdit={false}
              onToggleLocation={() => toggleLocation(location.id)}
              productId={productId}
            />
          ))}
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        <span className='font-montserrat text-base font-normal text-alba-black-500'>
          Comercialização
        </span>

        <div className='flex flex-col gap-3'>
          <div className='flex items-center justify-between rounded-2xl border border-alba-gray-200 px-5 py-4'>
            <div className='flex flex-col gap-0.5'>
              <span className='font-montserrat text-sm font-semibold text-alba-black-500'>
                Percentual de Corretagem do seguro
              </span>
              <span className='font-montserrat text-sm text-alba-gray-500'>
                Percentual
              </span>
            </div>
            <span className='font-montserrat text-sm text-alba-gray-500'>
              {formatPercent((calculation.commissionPercent || 0) / 100)}
            </span>
          </div>

          <div className='flex items-center justify-between rounded-2xl border border-alba-gray-200 px-5 py-4'>
            <div className='flex flex-col gap-0.5'>
              <span className='font-montserrat text-sm font-semibold text-alba-black-500'>
                Corretor
              </span>
              <span className='font-montserrat text-sm text-alba-gray-500'>
                {brokerName}
              </span>
            </div>
            <span className='font-montserrat text-sm text-alba-gray-500'>
              {formatPercent((calculation.participationPercent || 0) / 100)}
            </span>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6'>
        <Button
          variant='ghost'
          size='lg'
          className='flex w-full items-center justify-center gap-2 sm:w-auto'
          onClick={onBack}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
          <Button
            variant='secondary'
            size='md'
            className='flex w-full items-center justify-center gap-2 rounded-xl border-alba-error-red-500 bg-alba-error-red-500 px-6 py-2 text-alba-white-500 [&>svg]:text-alba-white-500 hover:bg-alba-error-red-600 sm:w-auto'
            onClick={() => onReject(observations)}
            disabled={isSubmitting}
          >
            <XCircle className='h-5 w-5' strokeWidth={1.6} />
            Recusar
          </Button>
          <Button
            variant='primary'
            size='md'
            className='flex w-full items-center gap-2 rounded-xl px-6 py-2 sm:w-auto'
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size='sm' colorScheme='blue' />
                Calculando...
              </>
            ) : (
              'Enviar Cálculo'
            )}
          </Button>
          <Button
            variant='primary'
            size='md'
            className='w-full rounded-xl px-6 py-2 sm:w-auto'
            onClick={onExit}
            disabled={isSubmitting}
          >
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
}
